import { call, put } from "@redux-saga/core/effects";
import { CardTypeConsentDTO, PersonApi } from "../../../generated/api";
import { getAPIConfig } from "../../../util/api-config";
import { handleError } from "../../../util/errorHandler";
import store from "../../configureStore";
import {
  RESET_PERSON_NAME,
  setGroupForPerson,
  setNameLoading,
  setPersonConsent,
  setPersonName,
} from "../../reducers/personReducer";
import confirmSaga from "./modalSagas";
import {
  GET_CARD_WITH_HISTORY_FOR_PERSON,
  RESET_CARDS,
} from "../../reducers/cardReducer";
import { toast, ToastContent } from "react-toastify";
import i18n from "../../../i18n/i18n";

function setUpClient() {
  return new PersonApi(
    getAPIConfig(store.getState().settings.internalSettings)
  );
}

export function* handleGetPersonName(action: any): any {
  try {
    const personClient = setUpClient();
    yield put(setNameLoading(true));
    const response = yield personClient.getNameForPerson(
      action.carePrincipalId,
      action.personId
    );
    yield put(setPersonName(response));
    yield put(setNameLoading(false));
  } catch (error: any) {
    yield put(setNameLoading(false));
    if (error?.status === 504) {
      toast.error(String(i18n.t("errors.nameTimeOut")));
    } else {
      yield handleError(error);
    }
  }
}

export function* handleGetPersonConsent(action: any): any {
  try {
    const personClient = setUpClient();
    const response: CardTypeConsentDTO = yield personClient.getPersonsConsent(
      action.carePrincipalId,
      action.personId,
      action.cardTypeId
    );

    const personConsent = {
      exportRestriction: response.exportRestriction,
      recurringCard: response.recurringCard,
      sendPostToCarePrincipal: response.sendPostToCarePrincipal,
      optIn: response.consents?.some(
        (c) => c.hsaId === action.carePrincipalId && c.optIn === true
      ),
      optOut: response.consents?.some(
        (c) => c.hsaId === action.carePrincipalId && c.optOut === true
      ),
      readConsent: response.consents?.some(
        (c) =>
          c.hsaId ===
            store.getState().session.currentUser.healthCareProviderHsaId &&
          c.readConsent === true
      ),
      readConsentOptOut: response.readConsentOptOut
        ? response.readConsentOptOut.readConsentOptOut
        : false,
    };
    yield put(setPersonConsent(personConsent));
  } catch (error: any) {
    yield handleError(error);
    const consentDefault = {
      exportRestriction: false,
      recurringCard: false,
      sendPostToCarePrincipal: false,
      optOut: false,
    };
    yield put(setPersonConsent(consentDefault));
  }
}

export function* handleSavePersonConsent(action: any): any {
  try {
    const personClient = setUpClient();
    yield personClient.updatePersonsConsent(
      action.params.carePrincipalId,
      action.params.personId,
      action.params.cardTypeId,
      action.params.consent
    );
    const carePrincipalId = action.params.carePrincipalId;
    const cardTypeId = action.params.cardTypeId;
    const personId = action.params.personId;
    toast.success(String(i18n.t("toasts.consentUpdated")));
    yield put({
      type: "GET_PERSON_CONSENT",
      carePrincipalId,
      cardTypeId,
      personId,
    });
    const params = action.params;

    yield put({ type: "GET_EXTENDED_CARD_FOR_PERSON", params });
  } catch (error) {
    yield handleError(error);
  }
}

export function* handleRegisterOptOut(action: any): any {
  try {
    const isConfirmed = yield call(confirmSaga, "registerOptOut");
    if (isConfirmed) {
      const personClient = setUpClient();
      let optOut = {
        sendPostToCarePrincipal: false,
        exportRestriction: false,
        recurringCard: false,
        optIn: false,
        optOut: true,
      };
      let consent = action.params?.consent ? action.params.consent : optOut;
      yield personClient.updatePersonsConsent(
        action.params.carePrincipalId,
        action.params.personId,
        action.params.cardTypeId,
        consent
      );
      yield put({ type: RESET_CARDS });
      yield put({ type: RESET_PERSON_NAME });
      toast.success(
        String(i18n.t("toasts.registeredOptOut", { personId: action.params.personId }))
      );
    }
  } catch (error) {
    yield handleError(error);
  }
}

export function* handleRegisterOptIn(action: any): any {
  try {
    const consent = {
      exportRestriction: false,
      recurringCard: false,
      sendPostToCarePrincipal: false,
      optOut: false,
      optIn: true,
    };
    const personClient = setUpClient();
    yield personClient.updatePersonsConsent(
      action.params.carePrincipalId,
      action.params.personId,
      action.params.cardTypeId,
      consent
    );
    yield put(setPersonConsent(consent));
  } catch (error) {
    yield handleError(error);
  }
}

export function* handleChangePersonConsentModal(action: any): any {
  try {
    yield call(confirmSaga, "changeConsent");
  } catch (error) {
    yield handleError(error);
  }
}

export function* handleGetGroupForPerson(action: any): any {
  try {
    const personClient = setUpClient();
    const response = yield personClient.getGroupForPerson(
      action.params.personId,
      action.pageable.size,
      action.pageable.page,
      action.pageable.sort
    );
    yield put(setGroupForPerson(response));
    yield call(confirmSaga, "groupHistory");
  } catch (error) {
    yield handleError(error);
  }
}

export function* handleGroupPerson(action: any): any {
  try {
    const isConfirmed = yield call(confirmSaga, "group");
    if (isConfirmed) {
      const personClient = setUpClient();
      yield personClient.groupPerson(
      action.params.carePrincipalId,
      action.params.personToGroup,
      action.params.cardSystemId
    );
    const params = action.params;
    yield put({ type: GET_CARD_WITH_HISTORY_FOR_PERSON, params });
    toast.success(
      String(i18n.t("toasts.personGrouped", { person: action.params.personToGroup }))
    );
    }
  } catch (error) {
    yield handleError(error);
    }
}

export function* handleUnGroupPerson(action: any): any {
  try {
    const isConfirmed = yield call(confirmSaga, "unGroup");
    if (isConfirmed) {
    const personClient = setUpClient();
    yield personClient.unGroupPerson(
      action.params.carePrincipalId,
      action.params.personToUnGroup,
      action.params.cardSystemId
    );
    const params = action.params;
    yield put({ type: GET_CARD_WITH_HISTORY_FOR_PERSON, params });
    toast.success(
      String(i18n.t("toasts.personUngrouped", {
        person: action.params.personToUnGroup,
      }))
    );
    }
  } catch (error) {
    yield handleError(error);
  }
}
